// Local (IndexedDB) reads for the register — all queries hit Dexie, never the network.
// Mirrors the POS Room queries (category list, products-by-category, UPC/title/custom-code search).

import { db } from "./db";
import { normalizeProduct } from "../engine/normalizeProduct";
import * as searchIndex from "./searchIndex";

const lc = (v) => String(v == null ? "" : v).toLowerCase();
// Go now GROUP_CONCATs multi custom codes with '|' (commit a1febc1: custom_code/vcustom_code are
// pipe-delimited; the old product_custom_codes/vvariant_custom_codes fields were dropped), while
// variant slots stay comma-joined — split on BOTH so exact scan-code lookup matches every token.
const csv = (v) => String(v == null ? "" : v).split(/[,|]/).map((s) => s.trim()).filter(Boolean);

const trimStr = (v) => String(v == null ? "" : v).trim();

// VISIBILITY (ports InStorePaymentFragment ~8548): cat_show_status 0=both, 1=online-only,
// 2=register-only, 3=neither → register shows 0/2 (or blank). Lottery categories are included ONLY
// when the store's `ebt` register setting contains "4" (Android `resultEbt`); otherwise dropped.
// This same list is Android's `orderCreateViewModel.categoryList`, i.e. what isCategoryEnable
// checks a product against — so a lottery product is addable exactly when the category is listed.
const isRegisterCategory = (c, lotteryEnabled) => {
  const s = trimStr(c.cat_show_status);
  return (s === "" || s === "0" || s === "2") && (lotteryEnabled || trimStr(c.is_lottery) !== "1");
};

// ORDER — exact port of Android `CategoryRegisterOrder.kt` (Register2ViewModel.sortedByRegisterOrder):
//   key  = register_alternate_name, else alternate_name (trimmed, blank ⇒ none)
//   rank = int key ⇒ max(int,0); pure a-z key ⇒ base-26 value − 1 ("a"=0 … "z"=25, "aa"=26); else MAX
//   then normalizedKey (lower-cased key), then numeric id, then lower-cased title.
// ⚠️ NOT plain string order: "b" (rank 1) sorts BEFORE "aa" (rank 26) — a plain localeCompare put
// "aa" first, which is the "category sequence galat hai" QA report.
const RANK_MAX = Number.MAX_SAFE_INTEGER;
function registerCategoryOrderRank(orderKey) {
  const k = trimStr(orderKey).toLowerCase();
  if (!k) return RANK_MAX;
  if (/^[+-]?\d+$/.test(k)) return Math.max(Number(k), 0);
  if (!/^[a-z]+$/.test(k)) return RANK_MAX;
  let rank = 0;
  for (const ch of k) rank = rank * 26 + (ch.charCodeAt(0) - 96);
  return rank - 1;
}
export function registerCategorySortKey(c) {
  const key = trimStr(c.register_alternate_name) || trimStr(c.alternate_name) || trimStr(c.alternateName);
  const id = Number(c.id);
  return {
    rank: registerCategoryOrderRank(key),
    normalizedKey: key.toLowerCase(),
    numericId: Number.isFinite(id) && /^\d+$/.test(trimStr(c.id)) ? id : RANK_MAX,
    title: lc(c.title || c.cat_name || ""),
  };
}
export function compareRegisterCategories(a, b) {
  const ka = registerCategorySortKey(a);
  const kb = registerCategorySortKey(b);
  if (ka.rank !== kb.rank) return ka.rank - kb.rank;
  if (ka.normalizedKey !== kb.normalizedKey) return ka.normalizedKey < kb.normalizedKey ? -1 : 1;
  if (ka.numericId !== kb.numericId) return ka.numericId - kb.numericId;
  if (ka.title !== kb.title) return ka.title < kb.title ? -1 : 1;
  return 0;
}
export async function getCategories({ lotteryEnabled = false } = {}) {
  const rows = await db.categories.toArray();
  return rows.filter((c) => c.is_deleted !== "1" && isRegisterCategory(c, lotteryEnabled)).sort(compareRegisterCategories);
}

// Android `Product.isCategoryEnable(registerCategoryIdList)`: at least one of the product's
// categories must be in the REGISTER category list (cat_show_status 0/2, non-lottery) — otherwise
// the POS refuses the add with "Product's category is disable from inventory." The scan / search
// paths reach products of hidden categories, so the grid filter alone is not enough.
export function isProductCategoryEnabled(product, registerCategories) {
  const ids = new Set((registerCategories || []).map((c) => String(c.id)));
  return String((product && product.cotegory) || "")
    .split(",")
    .some((id) => ids.has(id.trim()));
}

// Android addProductWithQuantity (OCV ~2565): a line's loyalty EARN / USE-point eligibility is the
// AND over the product's register categories — `earnPoint = categoryList.none { list.contains(cat.id)
// && !cat.earnPoint.isEnable() }`. `isEnable()` counts ONLY the string "1", so a category whose
// `earn_point` is "0"/blank disqualifies earning for every product in it (same for `use_point`).
// A product category id that isn't a register category is ignored (Android iterates categoryList).
// A line with no category (plain custom item) earns/uses nothing.
export function categoryLoyaltyFlags(product, registerCategories) {
  const ids = String((product && (product.cotegory ?? product.categoryId)) || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (!ids.length) return { canEarnPoint: false, canUsePoint: false };
  const byId = new Map((registerCategories || []).map((c) => [String(c.id), c]));
  let canEarnPoint = true;
  let canUsePoint = true;
  for (const id of ids) {
    const c = byId.get(id);
    if (!c) continue; // not a register category → doesn't disqualify
    if (String(c.earn_point) !== "1") canEarnPoint = false;
    if (String(c.use_point) !== "1") canUsePoint = false;
  }
  return { canEarnPoint, canUsePoint };
}

// Android CustomerSearchController.classifyCustomerSearch (utils/CustomerSearchController.kt) — the
// single source of truth for how the POS customer-search box routes typed text to a phone / email /
// name lookup. Priority order matters:
//   1. empty / contains '[' or ']'        -> "none"  (no lookup; '[' guards the "Name [pts]" display)
//   2. contains '@'                        -> "email"
//   3. has >=1 letter AND >=1 digit        -> "email" (e.g. "a1"; don't wait for '@')
//   4. letters only                        -> "name"
//   5. digits only                         -> "phone"
//   otherwise (punctuation/space only)     -> "none"
// (Kotlin uses Char.isLetter()/isDigit() = Unicode-aware, so \p{L} / \p{Nd} here, not [a-z]/\d.)
export function classifyCustomerSearch(raw) {
  const text = String(raw == null ? "" : raw);
  if (text === "" || text.includes("[") || text.includes("]")) return "none";
  if (text.includes("@")) return "email";
  const hasLetter = /\p{L}/u.test(text);
  const hasDigit = /\p{Nd}/u.test(text);
  if (hasLetter && hasDigit) return "email";
  if (hasLetter) return "name";
  if (hasDigit) return "phone";
  return "none";
}

// Stock-prompt follow-up (Android showDialogUpdateQuantity success): mirror the new on-hand quantity
// into the local catalog so the very next add reads the updated stock without waiting for a sync.
export async function updateLocalStock(productId, variantId, qty) {
  const p = await db.products.get(String(productId));
  if (!p) return;
  if (variantId && String(p.isvarient) === "1") {
    const ids = String(p.vvariant_id || "").split(",");
    const qtys = String(p.vquantity || "").split(",");
    const i = ids.findIndex((v) => v.trim() === String(variantId));
    if (i < 0) return;
    while (qtys.length < ids.length) qtys.push("0");
    qtys[i] = String(qty);
    p.vquantity = qtys.join(",");
  } else {
    p.quantity = String(qty);
  }
  await db.products.put(p);
}

// Products whose `cotegory` CSV contains catId. Bulk (mix&match) is joined per product below.
export async function getProductsByCategory(catId, limit = 500) {
  const id = String(catId);
  const out = [];
  await db.products
    .filter((p) => csv(p.cotegory).includes(id) && p.disable !== "1" && p.is_deleted !== "1")
    .each((p) => out.push(p));
  // ⚠️ SORT NUMERICALLY BY id, ASCENDING — do NOT rely on the store's own iteration order.
  //
  // Product ids arrive from the catalog bundle as STRINGS, so IndexedDB keys them as strings and
  // iterates lexicographically: "99871" lands above "103252". That scrambles the grid relative to the
  // catalog order merchants expect (Android hit exactly this and had to pin `ORDER BY` back to the
  // integer key). Ascending numeric id == the order the full sync inserted them, which is the
  // server's own cursor order.
  //
  // The `limit` slice happens AFTER the sort, deliberately: cutting first would pick the
  // lexicographically-first N — a different SET of products, not just a different order. That costs a
  // full scan of the filtered rows, which the Dexie `.filter()` above already performs anyway.
  out.sort((a, b) => {
    const na = Number(a.id);
    const nb = Number(b.id);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    return String(a.id).localeCompare(String(b.id));
  });
  return out.slice(0, limit);
}

// Contains-search over title / upc / custom_code / variant upc / variant name — matches Products_list.
// Fast path: in-memory index (searchIndex). Fallback: Dexie scan until the index is built.
export async function searchProducts(text, limit = 50) {
  const q = String(text == null ? "" : text).trim();
  if (!q) return [];
  if (searchIndex.isBuilt()) {
    const ids = searchIndex.searchNames(q, limit);
    if (!ids.length) return [];
    return (await db.products.bulkGet(ids)).filter(Boolean);
  }
  const lq = lc(q);
  const out = [];
  await db.products
    .filter((p) => {
      if (p.disable === "1" || p.is_deleted === "1") return false;
      return (
        lc(p.title).includes(lq) ||
        lc(p.upc).includes(lq) ||
        lc(p.custom_code).includes(lq) ||
        lc(p.vupc).includes(lq) ||
        lc(p.vcustom_code).includes(lq) ||
        lc(p.vvariant).includes(lq)
      );
    })
    .until(() => out.length >= limit)
    .each((p) => out.push(p));
  return out;
}

// Exact UPC/custom-code lookup for scan (product or any variant token). Returns the first match.
export async function findByCode(code) {
  const c = String(code || "").trim();
  if (!c) return null;
  if (searchIndex.isBuilt()) {
    const id = searchIndex.findCode(c);
    return id ? (await db.products.get(id)) || null : null;
  }
  const lcCode = c.toLowerCase();
  let hit = null;
  await db.products
    .filter((p) => {
      if (p.disable === "1" || p.is_deleted === "1") return false;
      return (
        p.upc === c ||
        csv(p.vupc).includes(c) ||
        csv(p.custom_code).some((x) => x.toLowerCase() === lcCode) ||
        csv(p.vcustom_code).some((x) => x.toLowerCase() === lcCode)
      );
    })
    .until(() => hit != null)
    .each((p) => {
      if (!hit) hit = p;
    });
  return hit;
}

// Tax id -> record map, for resolving product.other_taxes.
export async function getTaxMap() {
  const rows = await db.taxes.toArray();
  const map = new Map();
  rows.forEach((t) => map.set(String(t.id), t));
  return map;
}

// Normalize a tax id for matching — mirrors Android's
// `taxListItem.id.replace(" ","").equals(otherTaxesItem.replace(" ",""), ignoreCase=true)`.
const normTaxId = (v) => String(v == null ? "" : v).replace(/\s+/g, "").toLowerCase();

// Resolve a product's `other_taxes` CSV into the engine's per-line taxList. `other_taxes` carries
// ALL applicable tax ids INCLUDING DefaultTax (there is no separate default-tax add — this matches
// Android's addProductWithQuantity, which builds the line's taxList solely from other_taxes).
//
// Matching is whitespace- and case-insensitive on BOTH sides, so a stray space in a tax id — the
// classic CSV gotcha in this codebase — no longer silently drops the second/"other" tax on a
// multi-tax product. (Exact-match `taxMap.get()` used to drop it, leaving only DefaultTax.)
export function resolveTaxList(product, taxMap) {
  const ids = csv(product.other_taxes || product.otherTaxes);
  if (!ids.length) return [];
  // Normalized lookup over the tax settings (space-stripped, lowercased).
  const byNorm = new Map();
  for (const t of taxMap.values()) {
    if (t && t.id != null && String(t.id).trim() !== "") byNorm.set(normTaxId(t.id), t);
  }
  const list = [];
  const seen = new Set();
  for (const id of ids) {
    const key = normTaxId(id);
    if (!key || seen.has(key)) continue; // skip blanks + duplicate ids
    const t = byNorm.get(key);
    if (t) {
      seen.add(key);
      list.push({ id: t.id, title: t.title, displayName: t.displayname || t.displayName || null, percent: t.percent });
    }
  }
  return list;
}

export async function getBogos() {
  return db.bogos.toArray();
}

/**
 * The store's "Quickadd" category id — where a register-created product is filed.
 *
 * Matched by TITLE, exactly as Android does (`CategoryRegisterDao.getQuickAddId(title = "Quickadd")`).
 * Returns "" when the store has no such category, which the caller must treat as a hard stop: the
 * create endpoint requires a category and Android refuses with "QuickAdd Category is missing."
 */
export async function getQuickAddCategoryId() {
  const all = await db.categories.toArray();
  const hit = all.find((c) => String(c.title || c.name || "").trim().toLowerCase() === "quickadd");
  return hit ? String(hit.id) : "";
}

/**
 * Duplicate-code pre-check for Quick-Add. Android runs this BEFORE creating so the cashier gets
 * "Duplicate UPC found" on the field instead of a server rejection after the fact. Deliberately
 * checks product UPC, variant UPCs and custom codes — the same space `findByCode` scans — because the
 * catalog enforces uniqueness across all of them.
 */
export async function isDuplicateCode(code) {
  return !!(await findByCode(code));
}

export async function getCoupons() {
  return db.coupons.toArray();
}

export async function getMixMatches() {
  return db.matches.toArray();
}

// Mix&Match lookup — ports DatabaseRepository.getMixMatchMap: items_id is JSON {productId:[variantIds]};
// a variant entry keys "pid|vid", an EMPTY variant list keys "pid|" (whole product). Only enabled deals.
export async function buildMixMatchMap() {
  const map = new Map();
  const deals = await db.matches.toArray();
  for (const deal of deals) {
    if (!deal || String(deal.is_enable) !== "1" || !deal.items_id) continue;
    let items;
    try {
      items = JSON.parse(deal.items_id);
    } catch {
      continue; // malformed items_id → skip deal (Android swallows the parse error too)
    }
    if (Array.isArray(items)) {
      for (const pid of items) map.set(`${pid}|`, deal);
    } else if (items && typeof items === "object") {
      for (const [pid, variantIds] of Object.entries(items)) {
        if (Array.isArray(variantIds) && variantIds.length) {
          for (const vid of variantIds) map.set(`${pid}|${vid}`, deal);
        } else {
          map.set(`${pid}|`, deal);
        }
      }
    }
  }
  return map;
}

// Deal for a product (+optional variant) — variant products match by (pid,vid); plain by (pid,"").
export function mixMatchFor(mixMap, product, variant) {
  if (!mixMap || !product) return null;
  return (variant ? mixMap.get(`${product.id}|${variant.variantId}`) : mixMap.get(`${product.id}|`)) || null;
}

// Build the engine `model` (camelCase) for a raw stored product, optionally joining a mix&match deal.
export function toModel(product, bulk = null) {
  return normalizeProduct(product, bulk);
}
